'use client';

import type { CSSProperties } from 'react';
import CustomLink from 'components/shared/CustomLink';
import { useMarketingBannerOffset } from 'components/shared/Header/MarketingBanner';
import { campaignHeroCtaProps } from './v3/homepageCta';
import { Campaign, CampaignBannerComponent } from 'content/campaigns/types';
import { getSpeedsizeImageUrl } from '../shared/custom-image/CustomImage';
import v3 from './v3/v3.module.css';
import styles from './CampaignHeroBanner.module.css';

interface CampaignHeroBannerProps {
  campaign_banner_component: CampaignBannerComponent;
  product?: Campaign['product'];
  className?: string;
  /** When false, the background image loads lazily (off-screen carousel slides). */
  eager?: boolean;
}

export const CampaignHeroBanner = ({
  campaign_banner_component,
  product,
  className,
  eager = true,
}: CampaignHeroBannerProps) => {
  const bannerPx = useMarketingBannerOffset();
  const {
    variant,
    title,
    description,
    cta_text,
    cta_link,
    cta_variant,
    desktop_background_image_url,
    mobile_background_image_url,
  } = campaign_banner_component;

  // m1/blood-vision art went through the speedsize CDN (CustomImage); the
  // rest shipped raw unoptimized URLs — keep both behaviors.
  const compress = product === 'm1' || product === 'blood-vision';
  const rawMobile = mobile_background_image_url || desktop_background_image_url;
  const rawDesktop = desktop_background_image_url || mobile_background_image_url;
  const mobileSrc = compress ? getSpeedsizeImageUrl(rawMobile) : rawMobile;
  const desktopSrc = compress ? getSpeedsizeImageUrl(rawDesktop) : rawDesktop;

  // Handle relative URLs by prepending '/' if needed
  const href = cta_link.startsWith('http')
    ? cta_link
    : `/${cta_link.replace(/^\/+/, '')}`;
  const isOffsite =
    /^https?:\/\//i.test(href) && !/ultrahuman\.com/i.test(href);

  return (
    <section
      className={[styles.heroSection, className].filter(Boolean).join(' ')}
      data-header-theme="dark"
      style={{ '--campaign-banner-px': `${bannerPx}px` } as CSSProperties}
    >
      <div className={styles.contentSection} data-variant={variant || undefined}>
        <div
          className={styles.contentTextContainer}
          data-variant={variant || undefined}
        >
          {/* h2, not h1: the homepage's single h1 is the sr-only heading in
              home-client. main's Subtitle/ButtonAttachedSubtitle were styled.h1
              (HOMEPAGE_V3_MIGRATION DEFECT-2) — two h1s of *description* copy
              per live campaign. Headline is the h2, both subtitles are <p>. */}
          <h2 className={styles.title} data-variant={variant || undefined}>
            {title}
          </h2>
          {description && (
            <p className={styles.subtitle} data-variant={variant || undefined}>
              {description}
            </p>
          )}
          <CustomLink
            href={href}
            className={`${v3.pill} ${styles.cta}`}
            data-cta-variant={cta_variant ?? 'white'}
            data-layout={variant || undefined}
            rel={isOffsite ? 'noreferrer' : undefined}
            {...campaignHeroCtaProps(product)}
          >
            {cta_text}
          </CustomLink>
        </div>

        {description && variant === 'horizontal' && (
          <p
            className={styles.buttonAttachedSubtitle}
            data-variant={variant || undefined}
          >
            {description}
          </p>
        )}
      </div>
      <div className={styles.scrim} />
      {/* Art-directed so the banner is in SSR HTML (a useWindowSize gate
          previously returned null on the server) and only ONE crop loads.
          main ships two <Image>s toggled with display:none, which downloads
          both; <picture> is the same art direction for one request. */}
      <picture>
        <source media="(max-width: 767px)" srcSet={mobileSrc} />
        <img
          src={desktopSrc}
          alt={title}
          className="bg-image"
          loading={eager ? undefined : 'lazy'}
          fetchPriority={eager ? 'high' : undefined}
        />
      </picture>
    </section>
  );
};
